import { useEffect, useRef } from 'react'

import { cargarGoogleMaps, hayLlaveDeGoogle, ESTILO_MAPA } from '@/lib/mapas/google'

import estilos from './MapaDelSitio.module.css'

type Punto = { latitud: number; longitud: number }

type Props = {
  /** Dónde cae el trabajo. Nulo mientras nadie lo haya ubicado. */
  sitio: Punto | null
  /** Sobre qué centrar mientras no hay punto: la zona, o Cartago. */
  centro: Punto
  /** El radio del área de trabajo, en metros. El mismo que cuenta las horas. */
  radioM: number
  direccion: string
  /** Dónde va a estar ese día el colaborador que se está mirando, si se sabe. */
  cerca?: { latitud: number; longitud: number; etiqueta: string } | null
  /** Sin esto el mapa es de solo lectura. Con esto se arrastra y se hace clic. */
  onMover?: (latitud: number, longitud: number) => void
}

/**
 * El sitio en el mapa, a la hora de programar.
 *
 * Se dibujan tres cosas: el punto con su círculo —el área con la que después
 * se cuentan las horas, el mismo radio, no uno de adorno—, dónde va a estar
 * ese día la persona que se está considerando, y una línea entre los dos. La
 * línea es la que decide: dos puntos sueltos no dicen si eso queda de paso o
 * al otro lado del municipio.
 *
 * EL PUNTO SE ARRASTRA. Google no encuentra «Sendero Campestre casa 9» ni
 * «Chalet Los Naranjos», que es como se escriben las direcciones en Cartago —
 * y hasta ahora eso era el final del camino: sin punto no había geocerca, ni
 * detección de llegada, ni forma de saber quién quedaba cerca. Quien programa
 * conoce el sector; que lo ponga él.
 *
 * Y el gesto vale doble: un punto puesto a mano queda con origen «manual», y
 * fn_radio_del_sitio le da 80 m — el radio más apretado. Ajustar el punto
 * aprieta la geocerca sin que haya que pedirlo aparte.
 */
export function MapaDelSitio({ sitio, centro, radioM, direccion, cerca, onMover }: Props) {
  const contenedor = useRef<HTMLDivElement | null>(null)
  const mapa = useRef<google.maps.Map | null>(null)
  const dibujado = useRef<Array<{ setMap: (m: google.maps.Map | null) => void }>>([])

  // El callback vive en una referencia para no rehacer el mapa en cada render:
  // volver a crearlo perdería el zoom y el encuadre que la persona ajustó.
  const mover = useRef(onMover)
  mover.current = onMover

  useEffect(() => {
    if (!hayLlaveDeGoogle() || !contenedor.current) return

    let vivo = true

    void cargarGoogleMaps().then(({ maps }) => {
      if (!vivo || !contenedor.current) return

      const foco = sitio
        ? { lat: sitio.latitud, lng: sitio.longitud }
        : { lat: centro.latitud, lng: centro.longitud }

      if (!mapa.current) {
        mapa.current = new maps.Map(contenedor.current, {
        styles: ESTILO_MAPA,
          center: foco,
          // Sin punto se abre más cerrado: se está buscando una casa, no
          // mirando la ciudad.
          zoom: sitio ? 15 : 16,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          gestureHandling: 'greedy',
        })

        // Un clic en cualquier parte pone el punto ahí. Es más rápido que
        // arrastrar cuando todavía no hay nada que arrastrar.
        mapa.current.addListener('click', (evento: google.maps.MapMouseEvent) => {
          if (!evento.latLng || !mover.current) return
          mover.current(evento.latLng.lat(), evento.latLng.lng())
        })
      }

      for (const pieza of dibujado.current) pieza.setMap(null)
      dibujado.current = []

      if (sitio) {
        const marca = new google.maps.Marker({
          position: foco,
          map: mapa.current,
          title: onMover ? `${direccion} · arrástrelo para ajustarlo` : direccion,
          draggable: Boolean(onMover),
          cursor: onMover ? 'grab' : undefined,
        })

        if (onMover) {
          marca.addListener('dragend', (evento: google.maps.MapMouseEvent) => {
            if (!evento.latLng || !mover.current) return
            mover.current(evento.latLng.lat(), evento.latLng.lng())
          })
        }

        const area = new google.maps.Circle({
          map: mapa.current,
          center: foco,
          radius: radioM,
          strokeColor: '#7c3aed',
          strokeOpacity: 0.9,
          strokeWeight: 2,
          fillColor: '#7c3aed',
          fillOpacity: 0.12,
        })

        dibujado.current.push(marca, area)

        const limites = new google.maps.LatLngBounds()
        limites.extend(foco)

        if (cerca) {
          const otro = { lat: cerca.latitud, lng: cerca.longitud }

          const marcaOtro = new google.maps.Marker({
            position: otro,
            map: mapa.current,
            title: cerca.etiqueta,
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              scale: 7,
              fillColor: '#f97316',
              fillOpacity: 1,
              strokeColor: '#fff',
              strokeWeight: 2,
            },
          })

          const linea = new google.maps.Polyline({
            map: mapa.current,
            path: [foco, otro],
            strokeColor: '#f97316',
            strokeOpacity: 0,
            icons: [
              {
                icon: { path: 'M 0,-1 0,1', strokeOpacity: 0.9, scale: 3 },
                offset: '0',
                repeat: '12px',
              },
            ],
          })

          dibujado.current.push(marcaOtro, linea)
          limites.extend(otro)
          mapa.current.fitBounds(limites, 56)
        } else {
          mapa.current.setCenter(foco)
          mapa.current.fitBounds(area.getBounds()!, 40)
        }
      } else {
        mapa.current.setCenter(foco)
      }
    })

    return () => {
      vivo = false
    }
  }, [sitio, centro.latitud, centro.longitud, radioM, direccion, cerca, onMover])

  if (!hayLlaveDeGoogle()) {
    return (
      <div className={estilos.sinLlave}>
        Falta la llave de Google Maps para dibujar el mapa. Se programa igual; lo único que no se
        ve es dónde queda.
      </div>
    )
  }

  return (
    <div className={estilos.zona}>
      <div ref={contenedor} className={estilos.mapa} />

      {onMover && (
        <p className={estilos.instruccion}>
          {sitio
            ? 'Arrastre el punto para ajustarlo, o toque el mapa donde va.'
            : 'Toque el mapa donde queda la casa para ubicarla.'}
        </p>
      )}
    </div>
  )
}
